import { memo, useMemo } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { usePartnershipData } from './usePartnershipData.jsx';

export const FunderDistributionChart = memo(function FunderDistributionChart({ funderMap: mapProp }) {
  const { funderMap: mapHook, w3BilateralResults } = usePartnershipData();
  const yAxisWidth = useResponsiveYAxisWidth(180);
  const funderMap = mapProp ?? mapHook;

  const noDataFallback = (
    <ChartWrapper title="Funder Distribution">
      <NoChartData message="No funder data available" detail="This chart shows the distribution of funders from non-pooled (W3/Bilateral) projects. No funder information was found in the current selection." />
    </ChartWrapper>
  );

  if (!funderMap || funderMap.size === 0) return noDataFallback;

  const allFunders = useMemo(() => [...funderMap.values()].sort((a, b) => b.resultCount - a.resultCount), [funderMap]);

  const chartData = allFunders
    .slice(0, 10)
    .map(f => ({
      name: f.funderName.length > 30 ? f.funderName.substring(0, 28) + '...' : f.funderName,
      fullName: f.funderName,
      results: f.resultCount,
      grants: f.grantCount,
    }));

  if (chartData.length === 0) return noDataFallback;

  const totalResults = allFunders.reduce((s, f) => s + f.resultCount, 0);
  const totalGrants = allFunders.reduce((s, f) => s + f.grantCount, 0);
  const showing = Math.min(10, allFunders.length);
  const subtitle = `Showing top ${showing} of ${allFunders.length} W3/Bilateral funders · ${totalResults.toLocaleString()} results across ${totalGrants.toLocaleString()} grants`;

  return (
    <ChartWrapper title="Funder Distribution" info="Top funders of non-pooled (W3/Bilateral) results, ranked by the number of results their grants support." chartData={chartData} sourceResults={w3BilateralResults} exportFilename="funder-distribution">
      <p className="text-xs text-[var(--text-tertiary)] -mt-2 mb-3">{subtitle}</p>
      <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 32)}>
        <BarChart data={chartData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 180 ? 9 : 10 }} width={yAxisWidth} />
          <Tooltip content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return (
              <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{d.fullName}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F37021', display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ color: '#4b5563' }}>Results: <strong>{d.results?.toLocaleString()}</strong></span>
                </div>
                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>{d.grants} grant{d.grants !== 1 ? 's' : ''}</div>
              </div>
            );
          }} />
          <Bar dataKey="results" fill="#F37021" radius={[0, 4, 4, 0]}>
            <LabelList dataKey="results" position="right" {...LABEL_STYLE} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
